import React, { useMemo } from 'react';

import { EventInfo, isTeamEvent } from '@/api/events/types/types';
import { CarouselEventHud } from '@/components/event-hud/CarouselEventHud';
import { EventHudDetails, HudEvent } from '@/components/event-hud/EventHudDetails';
import { Box } from '@/components/lib/components';
import { useEvent } from '@/feature/event-details-sbk/hooks/use-event';
import { HUD_HEIGHTS } from '@/feature/event-details-sbk/utils/get-hud-height';
import { useEventSupportsScoreboard } from '@/hooks/use-event-supports-scoreboard';
import { designSystem } from '@/styles/styles';
import { Sport } from '@/types/api.generated';
import { gameUtils } from '@/utils/games';
import '@/utils/league';

type PickemEventHudProps = {
    event?: EventInfo;
};

const pickemSportToSbkSportMap = {
    [Sport.AmericanFootball]: 'American Football',
    [Sport.Baseball]: '',
    [Sport.Basketball]: '',
    [Sport.Golf]: '',
    [Sport.Mma]: '',
    [Sport.Hockey]: '',
    [Sport.Soccer]: '',
} as const;

export const PickemEventHud = ({ event: pickemEvent }: PickemEventHudProps) => {
    const pamEventId = pickemEvent?.dataFeedSourceIds.find(({ source }) => source === 'PAM')?.id;
    const isLive = pickemEvent && gameUtils.isLive(pickemEvent);
    const showScoreboard = useEventSupportsScoreboard(pickemEvent);

    const hudEvent = useMemo(() => convertPickemEventToHudEvent(pickemEvent), [pickemEvent]);

    if (!isTeamEvent(pickemEvent) || !hudEvent) {
        return null;
    }

    return showScoreboard && pamEventId && isLive ? (
        //Only show the event carousel hud for live events and when the scoreboard is enabled
        <PickemEventCarouselHud pamEventId={pamEventId} hudEvent={hudEvent} />
    ) : (
        //If the event is not LIVE, pass undefined so we dont fetch data un-necesarily
        <Box height={HUD_HEIGHTS.prematchHeader}>
            <EventHudDetails event={hudEvent} pamEventId={isLive ? pamEventId : undefined} />
        </Box>
    );
};

/**
 * Event HUD fetching the PAM data, and display a carousel with the event score details,
 * and the scoreboard on the second slide.
 * If the event is not LIVE in PAM yet, fallback to the default hud component
 */
const PickemEventCarouselHud = ({ pamEventId, hudEvent }: { pamEventId: string; hudEvent: HudEvent }) => {
    const { data: event } = useEvent(pamEventId);

    return event?.status === 'LIVE' ? (
        <Box height={HUD_HEIGHTS.liveHeader}>
            <CarouselEventHud event={hudEvent} pamEventId={pamEventId} />
        </Box>
    ) : (
        <Box height={HUD_HEIGHTS.prematchHeader}>
            <EventHudDetails event={hudEvent} pamEventId={pamEventId} />
        </Box>
    );
};

export const convertPickemEventToHudEvent = (event?: EventInfo): HudEvent | null => {
    if (!isTeamEvent(event)) {
        return null;
    }

    return {
        startTime: new Date(event.date).getTime(),
        // the component handles some sports differently
        // this component comes from SBK and has a different sport type
        // we need to map our sport to the SBK sport type
        sport: pickemSportToSbkSportMap[event.sport] || '',
        awayTeam: {
            alias: event.teams[0].name,
            logoUrl: event.teams[0].largeIcon,
            primaryColor: event.teams[0].color || designSystem.colors.gray3,
            secondaryColor: event.teams[0].secondaryColor || designSystem.colors.gray8,
            shortCode: event.teams[0].name,
        },
        homeTeam: {
            alias: event.teams[1].name,
            logoUrl: event.teams[1].largeIcon,
            primaryColor: event.teams[1].color || designSystem.colors.gray3,
            secondaryColor: event.teams[1].secondaryColor || designSystem.colors.gray8,
            shortCode: event.teams[1].name,
        },
    };
};
